import React, { useEffect, useState } from "react";
import { Modal, Spin, Alert, Button, Descriptions } from "antd";
import { EyeOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { getPublicGraveProfile, type PublicGraveProfile } from "./gisApi";

export interface PublicPreviewModalProps {
  open: boolean;
  cemeterySlug: string;
  graveCode: string;
  onClose: () => void;
}

/**
 * Modal xem trước giao diện tra cứu công khai cho thân nhân (S12.4 / S12.5.3).
 * Đảm bảo Data Masking: Không để lộ số CCCD, tài liệu nhạy cảm hay mã kỹ thuật.
 */
export function PublicPreviewModal({
  open,
  cemeterySlug,
  graveCode,
  onClose,
}: PublicPreviewModalProps): React.JSX.Element {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<PublicGraveProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !cemeterySlug || !graveCode) {
      setProfile(null);
      setError(null);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    getPublicGraveProfile(cemeterySlug, graveCode)
      .then((data) => {
        if (active) {
          setProfile(data);
        }
      })
      .catch((err) => {
        if (active) {
          setError((err as Error)?.message || "Không thể tải thông tin hồ sơ công khai.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [open, cemeterySlug, graveCode]);

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 text-emerald-800 font-bold">
          <EyeOutlined />
          <span>Giao diện hiển thị tra cứu cho thân nhân</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose} className="font-semibold">
          Đóng
        </Button>,
      ]}
      width={500}
      destroyOnClose
    >
      <div className="py-2 space-y-3">
        <Alert
          type="success"
          showIcon
          icon={<SafetyCertificateOutlined />}
          message="Đã bảo vệ thông tin riêng tư"
          description="Giao diện đã tự động che số CCCD, dữ liệu sinh trắc học và các mã kỹ thuật nội bộ của hệ thống."
          className="bg-emerald-50 border-emerald-200 text-xs"
        />

        {loading && (
          <div className="py-8 flex justify-center items-center">
            <Spin tip="Đang tải dữ liệu công khai..." />
          </div>
        )}

        {error && (
          <Alert type="error" showIcon message="Lỗi tải dữ liệu" description={error} />
        )}

        {!loading && profile && (
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3">
            <div className="border-b border-slate-200 pb-2">
              <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">
                Thông tin liệt sĩ
              </div>
              <div className="text-base font-bold text-slate-900 mt-0.5">
                {profile.kind === "unidentified"
                  ? "Liệt sĩ chưa xác định danh tính"
                  : profile.martyrName || "Chưa có thông tin họ tên"}
              </div>
            </div>

            <Descriptions column={2} size="small" className="text-xs">
              <Descriptions.Item label="Năm sinh / hy sinh">
                {profile.birthYear || profile.deathYear
                  ? `${profile.birthYear ?? "—"} / ${profile.deathYear ?? "—"}`
                  : "Chưa có thông tin"}
              </Descriptions.Item>

              <Descriptions.Item label="Quê quán">
                {profile.hometown || "Chưa có thông tin"}
              </Descriptions.Item>

              <Descriptions.Item label="Vị trí an táng" span={2}>
                <span className="font-semibold text-blue-700">
                  {profile.positionPath || profile.graveCode || "—"}
                </span>
              </Descriptions.Item>

              <Descriptions.Item label="Nghĩa trang an táng" span={2}>
                <span className="font-medium text-slate-800">
                  {profile.cemeteryName || "Nghĩa trang Liệt sĩ"}
                </span>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </div>
    </Modal>
  );
}
